import emojiStrings from './getEmojiList.locstring.json';
import loc from 'owa-localize';
import Emoji from '../store/schema/Emoji';
import EmojiFamily from '../store/schema/EmojiFamily';
import { computed, IComputedValue } from 'mobx';

/**
 * Emojis in the People section that are not skin-tonable, either by design or due to
 * a lack of support. The values in the lookup table are unimportant, but needed for
 * constant time checking of unsupported emojis.
 * char - emoji codePoint to be checked
 */
function skintoneSupported(char: string): boolean {
    let unsupported = {
        '🧟': 0,
        '🧞‍': 0,
        '🤼': 0,
        '🤺': 0,
    };
    return unsupported[char] == null;
}

/**
 * To display an emoji in UI, add it to this list in the appropriate category,
 * ensuring that its title and keywords have an entry in localized strings indexed by key and
 * distinguisher, as described below.
 */
function getEmojisByCategories(): string[][] {
    let emojis = [];
    emojis[EmojiFamily.People] = [
        '👶',
        '🧒',
        '👦',
        '👧',
        '🧑',
        '👨',
        '👩',
        '👱‍♀️',
        '👱‍♂️',
        '🧔',
        '🙇‍♀️',
        '🙇‍♂️',
        '🤦‍♂️',
        '🤦‍♀️',
        '🤷‍♀️',
        '🤷‍♂️',
        '💁‍♀️',
        '💁‍♂️',
        '🙅‍♀️',
        '🙅‍♂️',
        '🙆‍♀️',
        '🙆‍♂️',
        '🙋‍♀️',
        '🙋‍♂️',
        '🙎‍♀️',
        '🙎‍♂️',
        '🙍‍♀️',
        '🙍‍♂️',
        '💇‍♀️',
        '💇‍♂️',
        '💆‍♀️',
        '💆‍♂️',
        '🧓',
        '👴',
        '👵',
        '👲',
        '🧕',
        '👳‍♀️',
        '👳‍♂️',
        '👮‍♀️',
        '👮‍♂️',
        '👷‍♀️',
        '👷‍♂️',
        '💂‍♀️',
        '💂‍♂️',
        '🕵️‍♀️',
        '🕵️‍♂️',
        '👩‍⚕️',
        '👨‍⚕️',
        '👩‍🌾',
        '👨‍🌾',
        '👩‍🍳',
        '👨‍🍳',
        '👩‍🎓',
        '👨‍🎓',
        '👩‍🎤',
        '👨‍🎤',
        '👩‍🏫',
        '👨‍🏫',
        '👩‍🏭',
        '👨‍🏭',
        '👩‍💻',
        '👨‍💻',
        '👩‍💼',
        '👨‍💼',
        '👩‍🔧',
        '👨‍🔧',
        '👩‍🔬',
        '👨‍🔬',
        '👩‍🎨',
        '👨‍🎨',
        '👩‍🚒',
        '👨‍🚒',
        '👩‍✈️',
        '👨‍✈️',
        '👩‍🚀',
        '👨‍🚀',
        '👩‍⚖️',
        '👨‍⚖️',
        '🤶',
        '🎅',
        '🧙‍♀️',
        '🧙‍♂️',
        '🧝‍♀️',
        '🧝‍♂️',
        '🧛‍♀️',
        '🧛‍♂️',
        '🧟',
        '🧞‍',
        '🧜‍♀️',
        '🧜‍♂️',
        '🧚‍♀️',
        '🧚‍♂️',
        '🧖‍♀️',
        '🧖‍♂️',
        '🤹‍♀️',
        '🤹‍♂️',
        '👼',
        '🤰',
        '🤱',
        '👸',
        '🤴',
        '👰',
        '🤵',
        '🏃‍♀️',
        '🏃‍♂️',
        '🚶‍♀️',
        '🚶‍♂️',
        '🚣‍♀️',
        '🚣‍♂️',
        '🧗‍♀️',
        '🧗‍♂️',
        '🏊‍♀️',
        '🏊‍♂️',
        '🤽‍♀️',
        '🤽‍♂️',
        '🧘‍♀️',
        '🧘‍♂️',
        '🏄‍♀️',
        '🏄‍♂️',
        '🛀',
        '⛹️‍♀️',
        '⛹️‍♂️',
        '🏋️‍♀️',
        '🏋️‍♂️',
        '🚴‍♀️',
        '🚴‍♂️',
        '🚵‍♀️',
        '🚵‍♂️',
        '🏇',
        '🏂',
        '🤺',
        '🤼',
        '🤸‍♀️',
        '🤸‍♂️',
        '🤾‍♀️',
        '🤾‍♂️',
        '🏌️‍♀️',
        '🏌️‍♂️',
        '💃',
        '🕺',
        '🤲',
        '🙌',
        '👏',
        '👋',
        '🤙',
        '👍',
        '👎',
        '👊',
        '✊',
        '🤛',
        '🤜',
        '✌',
        '👌',
        '✋',
        '🤚',
        '👐',
        '💪',
        '🙏',
        '🤝',
        '☝',
        '👆',
        '👇',
        '👈',
        '👉',
        '🖕',
        '🖐',
        '🤟',
        '🤘',
        '🤞',
        '🖖',
        '✍',
        '🤳',
        '💅',
    ];
    emojis[EmojiFamily.Smileys] = [
        '😀',
        '😬',
        '😁',
        '😂',
        '🤣',
        '😃',
        '😄',
        '😅',
        '😆',
        '😇',
        '😉',
        '😊',
        '🙂',
        '🙃',
        '☺️',
        '😋',
        '😌',
        '😍',
        '😘',
        '😗',
        '😙',
        '😚',
        '😜',
        '🤪',
        '🤨',
        '🧐',
        '😝',
        '🤑',
        '🤓',
        '😎',
        '🤩',
        '🤡',
        '🤠',
        '🤗',
        '😏',
        '😶',
        '😐',
        '😑',
        '😒',
        '🙄',
        '🤔',
        '🤥',
        '🤭',
        '🤫',
        '🤬',
        '🤯',
        '😳',
        '😞',
        '😟',
        '😠',
        '😡',
        '😕',
        '🙁',
        '☹',
        '😣',
        '😖',
        '😫',
        '😩',
        '😤',
        '😮',
        '😱',
        '😨',
        '😰',
        '😯',
        '😧',
        '😢',
        '😥',
        '🤤',
        '😪',
        '😓',
        '😭',
        '😵',
        '😲',
        '🤐',
        '🤢',
        '🤧',
        '🤮',
        '😷',
        '🤒',
        '🤕',
        '😴',
        '💤',
        '💩',
        '😈',
        '👿',
        '👹',
        '👺',
        '💀',
        '👻',
        '👽',
        '🤖',
        '😺',
        '😸',
        '😹',
        '😻',
        '😼',
        '😽',
        '🙀',
        '😿',
        '😾',
        '👄',
        '👅',
        '👂',
        '👃',
        '👁',
        '👀',
        '🧠',
        '👤',
        '👥',
        '🗣',
        '👯',
        '👫',
        '👬',
        '👭',
        '💑',
        '👩‍❤️‍👩',
        '👨‍❤️‍👨',
        '💏',
        '👩‍❤️‍💋‍👩',
        '👨‍❤️‍💋‍👨',
        '👪',
        '👨‍👩‍👧',
        '👨‍👩‍👧‍👦',
        '👨‍👩‍👦‍👦',
        '👨‍👩‍👧‍👧',
        '👩‍👩‍👦',
        '👩‍👩‍👧',
        '👩‍👩‍👧‍👦',
        '👩‍👩‍👦‍👦',
        '👩‍👩‍👧‍👧',
        '👨‍👨‍👦',
        '👨‍👨‍👧',
        '👨‍👨‍👧‍👦',
        '👨‍👨‍👦‍👦',
        '👨‍👨‍👧‍👧',
        '👩‍👦',
        '👩‍👧',
        '👩‍👧‍👦',
        '👩‍👦‍👦',
        '👩‍👧‍👧',
        '👨‍👦',
        '👨‍👧',
        '👨‍👧‍👦',
        '👨‍👦‍👦',
        '👨‍👧‍👧',
    ];
    emojis[EmojiFamily.Nature] = [
        '🐶',
        '🐱',
        '🐭',
        '🐹',
        '🐰',
        '🦊',
        '🐻',
        '🐼',
        '🐨',
        '🐯',
        '🦁',
        '🐮',
        '🐷',
        '🐽',
        '🐸',
        '🦑',
        '🐙',
        '🦐',
        '🐵',
        '🦍',
        '🙈',
        '🙉',
        '🙊',
        '🐒',
        '🐔',
        '🐧',
        '🐦',
        '🐤',
        '🐣',
        '🐥',
        '🦆',
        '🦅',
        '🦉',
        '🦇',
        '🐺',
        '🐗',
        '🐴',
        '🦄',
        '🐝',
        '🐛',
        '🦋',
        '🐌',
        '🐞',
        '🐜',
        '🦗',
        '🕷',
        '🦂',
        '🦀',
        '🐍',
        '🦎',
        '🦖',
        '🦕',
        '🐢',
        '🐠',
        '🐟',
        '🐡',
        '🐬',
        '🦈',
        '🐳',
        '🐋',
        '🐊',
        '🐆',
        '🦓',
        '🐅',
        '🐃',
        '🐂',
        '🐄',
        '🦌',
        '🐪',
        '🐫',
        '🦒',
        '🐘',
        '🦏',
        '🐐',
        '🐏',
        '🐑',
        '🐎',
        '🐖',
        '🐀',
        '🐁',
        '🐓',
        '🦃',
        '🕊',
        '🐕',
        '🐩',
        '🐈',
        '🐇',
        '🐿',
        '🦔',
        '🐾',
        '🐉',
        '🐲',
        '🌵',
        '🎄',
        '🌲',
        '🌳',
        '🌴',
        '🌱',
        '🌿',
        '☘',
        '🍀',
        '🎍',
        '🎋',
        '🍃',
        '🍂',
        '🍁',
        '🌾',
        '🌺',
        '🌻',
        '🌹',
        '🥀',
        '🌷',
        '🌼',
        '🌸',
        '💐',
        '🍄',
        '🌰',
        '🎃',
        '🐚',
        '🕸',
        '🌎',
        '🌍',
        '🌏',
        '🌕',
        '🌖',
        '🌗',
        '🌘',
        '🌑',
        '🌒',
        '🌓',
        '🌔',
        '🌚',
        '🌝',
        '🌛',
        '🌜',
        '🌞',
        '🌙',
        '⭐',
        '🌟',
        '💫',
        '✨',
        '☄',
        '☀️',
        '🌤',
        '⛅',
        '🌥',
        '🌦',
        '☁️',
        '🌧',
        '⛈',
        '🌩',
        '⚡',
        '🔥',
        '💥',
        '❄️',
        '🌨',
        '⛄',
        '☃',
        '🌬',
        '💨',
        '🌪',
        '🌫',
        '☂',
        '☔',
        '💧',
        '💦',
        '🌊',
    ];
    emojis[EmojiFamily.Food] = [
        '🍏',
        '🍎',
        '🍐',
        '🍊',
        '🍋',
        '🍌',
        '🍉',
        '🍇',
        '🍓',
        '🍈',
        '🍒',
        '🍑',
        '🍍',
        '🥥',
        '🥝',
        '🥑',
        '🥦',
        '🍅',
        '🍆',
        '🥒',
        '🥕',
        '🌶',
        '🥔',
        '🌽',
        '🍠',
        '🥜',
        '🍯',
        '🥐',
        '🍞',
        '🥖',
        '🥨',
        '🧀',
        '🥚',
        '🥓',
        '🥩',
        '🥞',
        '🍗',
        '🍖',
        '🍤',
        '🍳',
        '🍔',
        '🍟',
        '🥙',
        '🌭',
        '🍕',
        '🥪',
        '🥫',
        '🍝',
        '🌮',
        '🌯',
        '🥗',
        '🥘',
        '🍜',
        '🍲',
        '🍥',
        '🥠',
        '🍣',
        '🍱',
        '🍛',
        '🍙',
        '🍚',
        '🍘',
        '🍢',
        '🍡',
        '🍧',
        '🍨',
        '🍦',
        '🥧',
        '🍰',
        '🎂',
        '🍮',
        '🍬',
        '🍭',
        '🍫',
        '🍿',
        '🥟',
        '🍩',
        '🍪',
        '🥛',
        '🍺',
        '🍻',
        '🥂',
        '🍷',
        '🥃',
        '🍸',
        '🍹',
        '🍾',
        '🍶',
        '🍵',
        '🥤',
        '☕',
        '🍼',
        '🥄',
        '🍴',
        '🍽',
        '🥣',
        '🥡',
        '🥢',
    ];
    emojis[EmojiFamily.Travel] = [
        '🚗',
        '🚕',
        '🚙',
        '🚌',
        '🚎',
        '🏎',
        '🚓',
        '🚑',
        '🚒',
        '🚐',
        '🚚',
        '🚛',
        '🚜',
        '🛴',
        '🏍',
        '🚲',
        '🛵',
        '🚨',
        '🚔',
        '🚍',
        '🚘',
        '🚖',
        '🚡',
        '🚠',
        '🚟',
        '🚃',
        '🚋',
        '🚝',
        '🚄',
        '🚅',
        '🚈',
        '🚞',
        '🚂',
        '🚆',
        '🚇',
        '🚊',
        '🚉',
        '🛸',
        '🚁',
        '🛩',
        '✈️',
        '🛫',
        '🛬',
        '⛵',
        '🛥',
        '🚤',
        '⛴',
        '🛳',
        '🚀',
        '🛰',
        '💺',
        '🛶',
        '⚓',
        '🚧',
        '⛽',
        '🚏',
        '🚦',
        '🚥',
        '🏁',
        '🚢',
        '🎡',
        '🎢',
        '🎠',
        '🏗',
        '🌁',
        '🗼',
        '🏭',
        '⛲',
        '🎑',
        '⛰',
        '🏔',
        '🗻',
        '🌋',
        '🗾',
        '🏕',
        '⛺',
        '🏞',
        '🛣',
        '🛤',
        '🌅',
        '🌄',
        '🏜',
        '🏖',
        '🏝',
        '🌇',
        '🌆',
        '🏙',
        '🌃',
        '🌉',
        '🌌',
        '🌠',
        '🎇',
        '🎆',
        '🌈',
        '🏘',
        '🏰',
        '🏯',
        '🏟',
        '🗽',
        '🏠',
        '🏡',
        '🏚',
        '🏢',
        '🏬',
        '🏣',
        '🏤',
        '🏥',
        '🏦',
        '🏨',
        '🏪',
        '🏫',
        '🏩',
        '💒',
        '🏛',
        '⛪',
        '🕌',
        '🕍',
    ];
    emojis[EmojiFamily.Objects] = [
        '⚽',
        '🏀',
        '🏈',
        '⚾',
        '🎾',
        '🏐',
        '🏉',
        '🎱',
        '⛳',
        '🏓',
        '🏸',
        '🥅',
        '🏒',
        '🏑',
        '🏏',
        '🎿',
        '⛷',
        '⛸',
        '🥌',
        '🛷',
        '🏹',
        '🎣',
        '🥊',
        '🥋',
        '🕴',
        '🏆',
        '🎽',
        '🏅',
        '🎖',
        '🥇',
        '🥈',
        '🥉',
        '🎗',
        '🏵',
        '🎫',
        '🎟',
        '🎭',
        '🎨',
        '🎪',
        '🎤',
        '🎧',
        '🎼',
        '🎹',
        '🥁',
        '🎷',
        '🎺',
        '🎸',
        '🎻',
        '🎬',
        '🎮',
        '👾',
        '🎯',
        '🎲',
        '🎰',
        '🎳',
        '🧥',
        '👚',
        '👕',
        '👖',
        '👔',
        '👗',
        '👙',
        '👘',
        '💄',
        '💋',
        '👣',
        '👠',
        '👡',
        '👢',
        '👞',
        '👟',
        '🧦',
        '🧤',
        '🧣',
        '👒',
        '🎩',
        '🧢',
        '⛑',
        '🎓',
        '👑',
        '🎒',
        '👝',
        '👛',
        '👜',
        '💼',
        '👓',
        '🕶',
        '💍',
        '🌂',
        '🕋',
        '⛩',
        '⌚',
        '📱',
        '📲',
        '💻',
        '⌨',
        '🖥',
        '🖨',
        '🖱',
        '🖲',
        '🕹',
        '🗜',
        '💽',
        '💾',
        '💿',
        '📀',
        '📼',
        '📷',
        '📸',
        '📹',
        '🎥',
        '📽',
        '🎞',
        '📞',
        '☎️',
        '📟',
        '📠',
        '📺',
        '📻',
        '🎙',
        '🎚',
        '🎛',
        '⏱',
        '⏲',
        '⏰',
        '🕰',
        '⏳',
        '⌛',
        '📡',
        '🔋',
        '🔌',
        '💡',
        '🔦',
        '🕯',
        '🗑',
        '🛢',
        '💸',
        '💵',
        '💴',
        '💶',
        '💷',
        '💰',
        '💳',
        '💎',
        '⚖',
        '🔧',
        '🔨',
        '⚒',
        '🛠',
        '⛏',
        '🔩',
        '⚙',
        '⛓',
        '🔫',
        '💣',
        '🔪',
        '🗡',
        '⚔',
        '🛡',
        '🚬',
        '☠',
        '⚰',
        '⚱',
        '🏺',
        '🔮',
        '📿',
        '💈',
        '⚗',
        '🔭',
        '🔬',
        '🕳',
        '💊',
        '💉',
        '🌡',
        '🏷',
        '🔖',
        '🚽',
        '🚿',
        '🛁',
        '🔑',
        '🗝',
        '🛋',
        '🛌',
        '🛏',
        '🚪',
        '🛎',
        '🖼',
        '🗺',
        '⛱',
        '🗿',
        '🛍',
        '🛒',
        '🎈',
        '🎏',
        '🎀',
        '🎁',
        '🎊',
        '🎉',
        '🎎',
        '🎐',
        '🎌',
        '🏮',
        '✉️',
        '📩',
        '📨',
        '📧',
        '💌',
        '📮',
        '📪',
        '📫',
        '📬',
        '📭',
        '📦',
        '📯',
        '📥',
        '📤',
        '📜',
        '📃',
        '📑',
        '📊',
        '📈',
        '📉',
        '📄',
        '📅',
        '📆',
        '🗓',
        '📇',
        '🗃',
        '🗳',
        '🗄',
        '📋',
        '🗒',
        '📁',
        '📂',
        '🗂',
        '🗞',
        '📰',
        '📓',
        '📕',
        '📗',
        '📘',
        '📙',
        '📔',
        '📒',
        '📚',
        '📖',
        '🔗',
        '📎',
        '🖇',
        '✂️',
        '📐',
        '📏',
        '📌',
        '📍',
        '🚩',
        '🏳',
        '🏴',
        '🏳️‍🌈',
        '🔐',
        '🔒',
        '🔓',
        '🔏',
        '🖊',
        '🖋',
        '✒️',
        '📝',
        '✏️',
        '🖍',
        '🖌',
        '🔍',
        '🔎',
    ];
    emojis[EmojiFamily.Symbols] = [
        '💯',
        '❤️',
        '🧡',
        '💛',
        '💚',
        '💙',
        '💜',
        '🖤',
        '💔',
        '❣',
        '💕',
        '💞',
        '💓',
        '💗',
        '💖',
        '💘',
        '💝',
        '💟',
        '☮',
        '✝',
        '☪',
        '🕉',
        '☸',
        '✡',
        '🔯',
        '🕎',
        '☯',
        '☦',
        '🛐',
        '⛎',
        '♈',
        '♉',
        '♊',
        '♋',
        '♌',
        '♍',
        '♎',
        '♏',
        '♐',
        '♑',
        '♒',
        '♓',
        '🆔',
        '⚛',
        '🈳',
        '🈹',
        '☢',
        '☣',
        '📴',
        '📳',
        '🈶',
        '🈚',
        '🈸',
        '🈺',
        '🈷️',
        '✴️',
        '🆚',
        '🉑',
        '💮',
        '🉐',
        '㊙️',
        '㊗️',
        '🈴',
        '🈵',
        '🈲',
        '🅰️',
        '🅱️',
        '🆎',
        '🆑',
        '🅾️',
        '🆘',
        '⛔',
        '📛',
        '🚫',
        '❌',
        '⭕',
        '🛑',
        '💢',
        '♨️',
        '🚷',
        '🚯',
        '🚳',
        '🚱',
        '🔞',
        '📵',
        '❗',
        '❕',
        '❓',
        '❔',
        '‼️',
        '⁉️',
        '🔅',
        '🔆',
        '🔱',
        '⚜',
        '〽️',
        '⚠️',
        '🚸',
        '🔰',
        '♻️',
        '🈯',
        '💹',
        '❇️',
        '✳️',
        '❎',
        '✅',
        '💠',
        '🌀',
        '➿',
        '🌐',
        'Ⓜ️',
        '🏧',
        '🈂️',
        '🛂',
        '🛃',
        '🛄',
        '🛅',
        '♿',
        '🚭',
        '🚾',
        '🅿️',
        '🚰',
        '🚹',
        '🚺',
        '🚼',
        '🚻',
        '🚮',
        '🎦',
        '📶',
        '🈁',
        '🆖',
        '🆗',
        '🆙',
        '🆒',
        '🆕',
        '🆓',
        '0️⃣',
        '1️⃣',
        '2️⃣',
        '3️⃣',
        '4️⃣',
        '5️⃣',
        '6️⃣',
        '7️⃣',
        '8️⃣',
        '9️⃣',
        '🔟',
        '*️⃣',
        '⏏️',
        '▶️',
        '⏸',
        '⏭',
        '⏹',
        '⏺',
        '⏯',
        '⏮',
        '⏩',
        '⏪',
        '🔀',
        '🔁',
        '🔂',
        '◀️',
        '🔢',
        '🔼',
        '🔽',
        '⏫',
        '⏬',
        '➡️',
        '⬅️',
        '⬆️',
        '⬇️',
        '↗️',
        '↘️',
        '↙️',
        '↖️',
        '↕️',
        '↔️',
        '🔄',
        '↪️',
        '↩️',
        '⤴️',
        '⤵️',
        '#️⃣',
        'ℹ️',
        '🔤',
        '🔡',
        '🔠',
        '🔣',
        '🎵',
        '🎶',
        '〰️',
        '➰',
        '✔️',
        '🔃',
        '➕',
        '➖',
        '➗',
        '✖️',
        '💲',
        '💱',
        '©️',
        '®️',
        '™️',
        '🔚',
        '🔙',
        '🔛',
        '🔝',
        '🔜',
        '☑️',
        '🔘',
        '⚪',
        '⚫',
        '🔴',
        '🔵',
        '🔸',
        '🔹',
        '🔶',
        '🔷',
        '🔺',
        '▪️',
        '▫️',
        '⬛',
        '⬜',
        '🔻',
        '◼️',
        '◻️',
        '◾',
        '◽',
        '🔲',
        '🔳',
        '🔈',
        '🔉',
        '🔊',
        '🔇',
        '📣',
        '📢',
        '🔔',
        '🔕',
        '🃏',
        '🀄',
        '♠️',
        '♣️',
        '♥️',
        '♦️',
        '🎴',
        '💭',
        '🗯',
        '💬',
        '🗨',
        '🕐',
        '🕑',
        '🕒',
        '🕓',
        '🕔',
        '🕕',
        '🕖',
        '🕗',
        '🕘',
        '🕙',
        '🕚',
        '🕛',
        '🕜',
        '🕝',
        '🕞',
        '🕟',
        '🕠',
        '🕡',
        '🕢',
        '🕣',
        '🕤',
        '🕥',
        '🕦',
        '🕧',
    ];

    return emojis;
}

// NOTE: name for an emoji should be in lower case
let computedEmojiList: IComputedValue<Emoji[]> = computed(() => {
    let emojiCategories: string[][] = getEmojisByCategories();
    let res: Emoji[] = [];
    let processed = {};

    // because the enum is const, we cannot loop over
    // it in the typical fashion
    let categories: EmojiFamily[] = [
        EmojiFamily.People,
        EmojiFamily.Smileys,
        EmojiFamily.Nature,
        EmojiFamily.Food,
        EmojiFamily.Travel,
        EmojiFamily.Objects,
        EmojiFamily.Symbols,
    ];

    // Adds emojis from each category
    for (let categoryIndex = 0; categoryIndex < categories.length; categoryIndex++) {
        let category: EmojiFamily = categories[categoryIndex];
        let emojis = emojiCategories[category];
        for (let c = 0; c < emojis.length; c++) {
            let char = emojis[c];
            let key = getCodePoint(char);

            // The distinguisher separates emojis that start with the same 'base character'.
            // Unicode describes the base character as the first displaying emoji in an
            // emoji sequence. Distinguishers default to nothing, then start counting up
            // from 1 chronologically as duplicate base characters are found. Since chronology
            // has to be maintained, a iterative for loop is a must.
            let distinguisher: string = null;
            if (processed[key] == null) {
                processed[key] = 0;
            } else {
                processed[key] += 1;
                distinguisher = processed[key];
            }
            if (distinguisher) {
                key = key + distinguisher;
            }
            let emoji = emojiCharToObject(char, category, key);
            if (emoji != null) {
                res.push(emoji);
            }
        }
    }
    return res;
});

// the first keyword in locstrings should be the shortcode. storing
// this in the same file essentially halves the size of our owa-emoji-string
// dependencies, as opposed to storing names in a separate string file
// at the cost of extra calculations when rendering
function getEmojiNameFromKeywords(keywords: string) {
    return keywords.substring(0, keywords.indexOf(',')).replace(new RegExp(' ', 'g'), '_');
}

// given an emoji character, returns that emoji's base (first) character's hex key string
// eg. 🤗 -> 1f917, 👪 -> base=👨‍ -> 1f46a, 👨‍👩‍👧‍👦 -> base=👨‍ -> 1f46a
function getCodePoint(char: any): string {
    return char.codePointAt(0).toString(16);
}

// Given an emoji character (ie.😒), fetches relevant fields to build
// an emoji object. Returns null on failure.
function emojiCharToObject(char: string, category: EmojiFamily, key: string): Emoji {
    let emoji: Emoji = { key: key, family: category, codePoint: char };

    // locstrings titles emojis with format 'u[hexcode][distinguisher]'
    let keywords = loc(emojiStrings['u' + key]);
    if (!keywords) {
        return null;
    }
    let emojiName = getEmojiNameFromKeywords(keywords);
    emoji.description = emojiName;
    emoji.keywords = keywords;

    // All emojis in people category have colorable skin and genders
    if (category == EmojiFamily.People) {
        emoji.hasSkinTone = skintoneSupported(char);
    }
    return emoji;
}

/**
 * Get the complete emoji list
 */
export default function getEmojiList(): Emoji[] {
    return computedEmojiList.get();
}
